import { Injectable } from '@angular/core';

/**
 * Service responsible for converting dates between timezones and UTC.
 * Follows Single Responsibility Principle by focusing solely on timezone conversions.
 */
@Injectable({
  providedIn: 'root'
})
export class TimezoneConversionService {
  private readonly MAX_CONVERSION_ITERATIONS = 3;

  /**
   * Converts a date from a specific timezone to UTC.
   * Uses an iterative approach to find the correct UTC timestamp that,
   * when formatted in the target timezone, produces the desired date/time components.
   * 
   * @param date - The date to convert (components represent local time in target timezone)
   * @param timezone - IANA timezone identifier (e.g., "America/New_York")
   * @returns The equivalent UTC date
   */
  convertToUTC(date: Date, timezone: string): Date {
    if (!date || !timezone) {
      throw new Error('Date and timezone are required for conversion');
    }

    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();

    // Start with UTC date having these components as initial guess
    let candidateUTC = new Date(Date.UTC(year, month, day, hours, minutes, seconds));
    
    const formatter = this.createTimezoneFormatter(timezone);
    
    // Iterate to find the correct UTC time (usually converges in 1-2 iterations)
    for (let i = 0; i < this.MAX_CONVERSION_ITERATIONS; i++) {
      const tzParts = this.extractDateTimeParts(formatter, candidateUTC);
      
      if (this.isExactMatch(tzParts, year, month, day, hours, minutes, seconds)) {
        break;
      }
      
      const diffMs = this.calculateTimeDifference(
        year, month, day, hours, minutes, seconds,
        tzParts.year, tzParts.month, tzParts.day, tzParts.hour, tzParts.minute, tzParts.second
      );
      
      candidateUTC = new Date(candidateUTC.getTime() + diffMs);
    }
    
    return candidateUTC;
  }

  /**
   * Extracts IANA timezone identifier from a timezone string.
   * Handles formats like "America/New_York (GMT-05:00)"
   * 
   * @param timezoneString - The timezone string that may include offset information
   * @returns The IANA timezone identifier
   */
  extractIanaTimezone(timezoneString: string): string {
    if (!timezoneString) {
      throw new Error('Timezone string is required');
    }
    
    // Extract IANA timezone from the timezone string (format: "America/New_York (GMT-05:00)")
    return timezoneString.split(' ')[0];
  }

  /**
   * Creates a DateTimeFormatter for the specified timezone.
   * 
   * @param timezone - IANA timezone identifier
   * @returns Configured Intl.DateTimeFormat instance
   */
  private createTimezoneFormatter(timezone: string): Intl.DateTimeFormat {
    return new Intl.DateTimeFormat('en', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  }

  /**
   * Extracts date/time parts from a formatted date string.
   * 
   * @param formatter - The DateTimeFormatter to use
   * @param date - The date to format
   * @returns Object containing extracted date/time components
   */
  private extractDateTimeParts(
    formatter: Intl.DateTimeFormat,
    date: Date
  ): { year: number; month: number; day: number; hour: number; minute: number; second: number } {
    const parts = formatter.formatToParts(date);
    
    return {
      year: parseInt(parts.find(p => p.type === 'year')?.value || '0', 10),
      month: parseInt(parts.find(p => p.type === 'month')?.value || '0', 10) - 1,
      day: parseInt(parts.find(p => p.type === 'day')?.value || '0', 10),
      hour: parseInt(parts.find(p => p.type === 'hour')?.value || '0', 10),
      minute: parseInt(parts.find(p => p.type === 'minute')?.value || '0', 10),
      second: parseInt(parts.find(p => p.type === 'second')?.value || '0', 10)
    };
  }

  /**
   * Checks if the extracted parts match the target date/time components exactly.
   * 
   * @param parts - Extracted date/time parts
   * @param year - Target year
   * @param month - Target month
   * @param day - Target day
   * @param hours - Target hours
   * @param minutes - Target minutes
   * @param seconds - Target seconds
   * @returns True if all components match exactly
   */
  private isExactMatch(
    parts: { year: number; month: number; day: number; hour: number; minute: number; second: number },
    year: number,
    month: number,
    day: number,
    hours: number,
    minutes: number,
    seconds: number
  ): boolean {
    return parts.year === year &&
           parts.month === month &&
           parts.day === day &&
           parts.hour === hours &&
           parts.minute === minutes &&
           parts.second === seconds;
  }

  /**
   * Calculates the time difference in milliseconds between two date/time specifications.
   * 
   * @param year1 - First date year
   * @param month1 - First date month
   * @param day1 - First date day
   * @param hours1 - First date hours
   * @param minutes1 - First date minutes
   * @param seconds1 - First date seconds
   * @param year2 - Second date year
   * @param month2 - Second date month
   * @param day2 - Second date day
   * @param hours2 - Second date hours
   * @param minutes2 - Second date minutes
   * @param seconds2 - Second date seconds
   * @returns Difference in milliseconds
   */
  private calculateTimeDifference(
    year1: number, month1: number, day1: number, hours1: number, minutes1: number, seconds1: number,
    year2: number, month2: number, day2: number, hours2: number, minutes2: number, seconds2: number
  ): number {
    const targetUTC = new Date(Date.UTC(year1, month1, day1, hours1, minutes1, seconds1));
    const tzUTC = new Date(Date.UTC(year2, month2, day2, hours2, minutes2, seconds2));
    return targetUTC.getTime() - tzUTC.getTime();
  }
}

